/** ローカルセーブ。破損・未対応バージョンを「新規」として扱わない。 */
import { STORAGE } from './constants';
import { migrate } from './migration';

export interface StoredPlayerData extends Record<string, unknown> {
  gold: number;
  collection: Array<{ cardId: string; rarity: string; quantity: number }>;
  userDecks: Array<Record<string, unknown> & { cards: Array<{ cardId: string }> }>;
  stats: Record<string, unknown>;
}
interface Snapshot {
  version: number;
  data: StoredPlayerData;
  updatedAt?: number;
}
export type LoadResult =
  | { status: 'ok'; data: StoredPlayerData }
  | { status: 'missing' }
  | { status: 'corrupt' | 'unsupported' | 'unavailable'; message: string };

export const BACKUP_KEY = `${STORAGE.KEY}.backup`;
const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

/** 旧セーブの追加項目不足は修復可能にし、通貨・所持品等の破損は拒否する。 */
export function isStoredPlayerData(value: unknown): value is StoredPlayerData {
  if (!isObject(value) || typeof value.gold !== 'number' || !Number.isFinite(value.gold) || value.gold < 0) return false;
  if (!Array.isArray(value.collection) || !value.collection.every(item =>
    isObject(item) && typeof item.cardId === 'string' && item.cardId.length > 0
    && typeof item.rarity === 'string' && isCount(item.quantity))) return false;
  if (!Array.isArray(value.userDecks) || !value.userDecks.every(deck =>
    isObject(deck) && Array.isArray(deck.cards) && deck.cards.every(card =>
      isObject(card) && typeof card.cardId === 'string' && card.cardId.length > 0))) return false;
  if (!isObject(value.stats)) return false;
  if (value.unopenedPacks !== undefined && !isCount(value.unopenedPacks)) return false;
  return true;
}

export class SaveReadError extends Error {
  constructor(public readonly status: 'corrupt' | 'unsupported' | 'unavailable', message: string) {
    super(message);
    this.name = 'SaveReadError';
  }
}

function parseSnapshot(raw: string): Snapshot {
  let value: unknown;
  try { value = JSON.parse(raw); }
  catch { throw new SaveReadError('corrupt', 'セーブのJSONが壊れています。元データは上書きしていません。'); }
  if (!isObject(value) || !Number.isSafeInteger(value.version) || (value.version as number) < 1) {
    throw new SaveReadError('corrupt', 'セーブの形式・バージョン情報が不正です。');
  }
  if ((value.version as number) > STORAGE.VERSION) {
    throw new SaveReadError('unsupported', 'このセーブは新しいバージョン用です。対応するアプリで開いてください。');
  }
  if (!isStoredPlayerData(value.data)) {
    throw new SaveReadError('corrupt', 'セーブの通貨・所持カード・デッキ情報を読み取れません。');
  }
  return value as unknown as Snapshot;
}

/** 検証とシリアライズが完了してから、既存データを必要に応じて退避して置換する。 */
function replaceSnapshot(data: StoredPlayerData, backup: boolean, allowRecovery = false): void {
  const raw = JSON.stringify({ version: STORAGE.VERSION, data, updatedAt: Date.now() });
  parseSnapshot(raw); // NaNやシリアライズによる型変化も保存前に拒否する
  const previous = localStorage.getItem(STORAGE.KEY);
  if (previous !== null && !allowRecovery) parseSnapshot(previous);
  // バックアップ失敗時は本体を書き換えない。
  if (backup && previous !== null) localStorage.setItem(BACKUP_KEY, previous);
  localStorage.setItem(STORAGE.KEY, raw);
}

export const save = (data: unknown, options: { backup?: boolean } = {}): boolean => {
  try {
    if (!isStoredPlayerData(data)) throw new Error('Invalid player data');
    replaceSnapshot(data, options.backup ?? false);
    return true;
  } catch (error) {
    console.error('Failed to save player data:', error);
    return false;
  }
};

export const loadResult = (): LoadResult => {
  try {
    const raw = localStorage.getItem(STORAGE.KEY);
    if (raw === null) return { status: 'missing' };
    const snapshot = parseSnapshot(raw);
    let data = snapshot.data;
    if (snapshot.version < STORAGE.VERSION) {
      // 元のJSONを保ったまま、コピーに対してマイグレーション・検証する。
      data = migrate(data, snapshot.version, STORAGE.VERSION);
      if (!isStoredPlayerData(data)) throw new SaveReadError('corrupt', '移行後のセーブが不正です。');
      replaceSnapshot(data, true);
    }
    return { status: 'ok', data };
  } catch (error) {
    if (error instanceof SaveReadError) return { status: error.status, message: error.message };
    return { status: 'unavailable', message: '保存領域の読み書き、またはセーブの移行に失敗しました。元データを確認してください。' };
  }
};

/** 互換API。nullは本当に未作成の場合に限る。 */
export const load = (): StoredPlayerData | null => {
  const result = loadResult();
  if (result.status === 'ok') return result.data;
  if (result.status === 'missing') return null;
  throw new SaveReadError(result.status, result.message);
};

export const clear = (): boolean => {
  try { localStorage.removeItem(STORAGE.KEY); return true; }
  catch { return false; }
};
export const exists = (): boolean => localStorage.getItem(STORAGE.KEY) !== null;
export const exportData = (): string | null => localStorage.getItem(STORAGE.KEY);
export const exportBackup = (): string | null => localStorage.getItem(BACKUP_KEY);

/** インポート候補を検証してから置換。失敗した候補は既存セーブを壊さない。 */
export const importData = (jsonString: string): boolean => {
  try {
    const snapshot = parseSnapshot(jsonString);
    const data = snapshot.version < STORAGE.VERSION
      ? migrate(snapshot.data, snapshot.version, STORAGE.VERSION) : snapshot.data;
    if (!isStoredPlayerData(data)) throw new Error('Invalid migrated player data');
    replaceSnapshot(data, true, true);
    return true;
  } catch (error) {
    console.error('Failed to import player data:', error);
    return false;
  }
};

export const getLastUpdated = (): number | null => {
  try {
    const raw = localStorage.getItem(STORAGE.KEY);
    if (raw === null) return null;
    const timestamp = parseSnapshot(raw).updatedAt;
    return typeof timestamp === 'number' && Number.isFinite(timestamp) ? timestamp : null;
  } catch { return null; }
};

export const storage = {
  save, load, loadResult, clear, exists, export: exportData, exportBackup,
  import: importData, getLastUpdated,
};
export default storage;
